import { Col, Dropdown, Input, Popconfirm, Row, message } from 'antd';
import { DeleteOutlined, EditOutlined, MoreOutlined } from '@ant-design/icons';
import { pdfjs } from 'react-pdf';
import React, { useCallback, useEffect, useState } from 'react';
import {
  calculateExactAge,
  getAccessToken,
  getUserDetails,
  makePostCall,
  makeGetCall,
} from '../../utils/helper';
import './view-notes.scss';
import PdfViewer from '../../components/PdfViewer';
import { FieldLabels } from '../../constants/fieldLabels';
const moment = require('moment');

const iconStyle = {
  fontSize: '18px',
  cursor: 'pointer',
  color: '#595959',
};

const ALLOWED_FILE_TYPES = {
  pdf: 'application/pdf',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  gif: 'image/gif',
};

const getNormalizedNoteId = (noteId: string | number | null | undefined) =>
  noteId === null || noteId === undefined ? null : String(noteId);

const areSameNote = (firstNoteId: string | number | null | undefined, secondNoteId: string | number | null | undefined) =>
  getNormalizedNoteId(firstNoteId) === getNormalizedNoteId(secondNoteId);

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf-worker-min.js';

const ViewNotes = ({ viewNotesModal, handleNoteSelection, selectedNote, isEditMode, onEditNoteStateChange, onSaveNoteRegister }) => {
  const [prevNotes, setPrevNotes] = useState([]);
  const [pdfData, setPdfData] = useState(null);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [mimeType, setMimeType] = useState('');
  const [pendingDeleteNoteId, setPendingDeleteNoteId] = useState<string | number | null>(null);
  const [editingNoteId, setEditingNoteId] = useState<string | number | null>(null);
  const [editedNoteText, setEditedNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  const { TextArea } = Input;

  const { details } = viewNotesModal;
  const { pacs_order } = details;
  const { patient } = pacs_order;

  const user_id = getUserDetails()?.username;

  const fetchDocumentContent = useCallback(async (noteId: string | number) => {
    setLoadingPdf(true);
    try {
      const res = await makeGetCall(`/fetch-note-file-by-id?note_id=${noteId}`, {
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
        responseType: 'arraybuffer',
      });
      setPdfData(res.data);
    } catch (error) {
      console.error('Error loading PDF', error);
      setPdfData(null);
    } finally {
      setLoadingPdf(false);
    }
  }, []);

  const fetchPrevNotes = useCallback(async () => {
    const { pacs_order } = viewNotesModal?.details;
    const { patient } = pacs_order;
    const { pat_pin } = patient;

    const payload = {
      user_id,
      pin: pat_pin,
      order_id: pacs_order.his_ord_no,
      ord_no: pacs_order.pacs_ord_id,
    };

    try {
      const res = await makePostCall('/pat-prev-notes', payload);
      const notesData = res.data?.data || [];
      setPrevNotes(notesData);

      const flatNotes = notesData.flatMap(group => group?.rows || []);
      const currentSelectedId = selectedNote?.rn_id;
      const retainedIdKey = currentSelectedId === null || currentSelectedId === undefined ? null : String(currentSelectedId);
      const currentSelectedKey = currentSelectedId === null || currentSelectedId === undefined ? null : String(currentSelectedId);
      const matchedCurrent = retainedIdKey
        ? flatNotes.find(note => String(note?.rn_id) === retainedIdKey)
        : null;
      const nextSelection = matchedCurrent || flatNotes[0] || null;
      const nextSelectionKey =
        nextSelection?.rn_id === null || nextSelection?.rn_id === undefined
          ? null
          : String(nextSelection?.rn_id);

      const isSelectionChanged = nextSelectionKey !== currentSelectedKey;
      const isCurrentSelectionContentStale =
        !!matchedCurrent &&
        (matchedCurrent?.rn_notes || '') !== (selectedNote?.rn_notes || '');

      if (isSelectionChanged || isCurrentSelectionContentStale) {
        handleNoteSelection(nextSelection, viewNotesModal?.details);
      }
    } catch (e) {
      console.log('Error', e);
      setPrevNotes([]);
    }
  }, [viewNotesModal, user_id, selectedNote?.rn_id, handleNoteSelection]);

  useEffect(() => {
    fetchPrevNotes();
  }, [fetchPrevNotes]);

  useEffect(() => {
    const mtype = selectedNote?.rn_upload_path?.split('.').pop();

    const mymime = ALLOWED_FILE_TYPES[mtype];
    setMimeType(mymime);

    if (selectedNote?.rn_upload_path && selectedNote?.rn_upload_type !== 'notes' && selectedNote?.rn_id) {
      fetchDocumentContent(selectedNote.rn_id);
    } else {
      setPdfData(null);
    }

    if (selectedNote?.rn_upload_type === 'notes') {
      setEditedNoteText(selectedNote?.rn_notes || '');
    } else {
      setEditedNoteText('');
    }

    setEditingNoteId(null);
    if (onEditNoteStateChange) {
      onEditNoteStateChange(false);
    }
  }, [selectedNote, fetchDocumentContent, onEditNoteStateChange]);

  const onEditNote = (note, details) => {
    console.log('Edit Note', note, details);

    if (note?.rn_upload_type !== 'notes') {
      message.warning('Only text notes can be edited');
      return;
    }

    setEditingNoteId(note?.rn_id);
    setEditedNoteText(note?.rn_notes || '');
    if (onEditNoteStateChange) {
      onEditNoteStateChange(true);
    }

  };

  const saveEditedNote = useCallback(async () => {
    if (!editingNoteId || selectedNote?.rn_upload_type !== 'notes') {
      message.info('No note selected for editing');
      return false;
    }

    const updatedText = editedNoteText?.trim();
    if (!updatedText) {
      message.warning('Note cannot be empty');
      return false;
    }

    setSavingNote(true);
    try {
      if (areSameNote(selectedNote?.rn_id, editingNoteId)) {
        handleNoteSelection(
          {
            ...selectedNote,
            rn_notes: updatedText,
          },
          viewNotesModal?.details
        );
      }

     await makePostCall('/save-note', {
        note_id: editingNoteId,
        notes: updatedText,
        user_id,
      });
      message.success('Note saved successfully');
      await fetchPrevNotes();
      setEditingNoteId(null);
      if (onEditNoteStateChange) {
        onEditNoteStateChange(false);
      }
      return true;
    } catch (error) {
      console.log('Error saving note', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to save note';
      message.error(errorMessage);
      return false;
    } finally {
      setSavingNote(false);
    }
  }, [
    editingNoteId,
    editedNoteText,
    selectedNote,
    user_id,
    onEditNoteStateChange,
    handleNoteSelection,
    viewNotesModal,
    fetchPrevNotes,
  ]);

  useEffect(() => {
    if (onSaveNoteRegister) {
      onSaveNoteRegister(() => saveEditedNote);
    }
  }, [onSaveNoteRegister, saveEditedNote]);

  useEffect(() => {
    if (isEditMode === false && editingNoteId !== null) {
      setEditingNoteId(null);
      if (selectedNote?.rn_upload_type === 'notes') {
        setEditedNoteText(selectedNote?.rn_notes || '');
      } else {
        setEditedNoteText('');
      }
    }
  }, [isEditMode, editingNoteId, selectedNote]);

  const onDeleteNote = async (note, details) => {
    try {
      const payload = {
        note_id: note?.rn_id,
      };

      await makePostCall('/delete-note', payload);
      message.success('Note deleted successfully');
      fetchPrevNotes();
    } catch (error) {
      console.log('Error deleting note', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to delete note';
      message.error(errorMessage);
    }
  };

  return (
    <div className="view-notes-container">
      <div className="notes-header">RIS NOTES</div>
      <Row>
        <Col span={8}>
          <div className="info-list" style={{ height: "125px" }}>
            <div className="info-item">
              <span className="notes-label">{FieldLabels.PATIENT_NAME}: </span>
              <span className="notes-value">{patient?.pat_name}</span>
            </div>
            <div className="info-row">
              <div className="info-item">
                <span className="notes-label">{FieldLabels.PATIENT_ID}: </span>
                <span className="notes-value">{patient?.pat_pin}</span>
              </div>
              <div className="info-item">
                <span className="notes-label">{FieldLabels.PATIENT_REPORT_AGE}: </span>
                <span className="notes-value">{calculateExactAge(patient?.pat_dob)}</span>
              </div>
            </div>
            <div className="info-item">
              <span className="notes-label">{FieldLabels.ORDER_NUMBER}: </span>
              <span className="notes-value">{pacs_order?.po_his_ord_no}</span>
            </div>
            <div className="info-item">
              <span className="notes-label">Accession No: </span>
              <span className="notes-value">{pacs_order?.po_acc_no}</span>
            </div>
            <div className="info-item">
              <span className="notes-label">{FieldLabels.REFERRING_PHYSICIAN}: </span>
              <span className="notes-value">{pacs_order?.po_ref_doc}</span>
            </div>
          </div>
          <div className="notes-list" style={{ height: '300px' }}>
            {prevNotes?.map(dateNote => (
              <div key={dateNote.creationdate}>
                <div className="notes-date">{dateNote.creationdate}</div>
                {dateNote?.rows?.map(note => (
                  <div
                    key={note.rn_id}
                    className={`notes-list-item ${areSameNote(note.rn_id, selectedNote?.rn_id) ? 'selected' : ''}`}
                    onClick={() => {
                      if (!areSameNote(note.rn_id, selectedNote?.rn_id)) {
                        handleNoteSelection(note, viewNotesModal?.details);
                      }
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span>
                        {`${note.rn_upload_type?.toUpperCase()} ${note.rn_file_name ? ' | ' + note.rn_file_name : ''} | ${moment(note.createdAt).format('DD/MM/YYYY HH:mm:ss') || ''}`}
                      </span>
                      <Dropdown
                        overlayClassName="view-notes-options-menu"
                        placement="bottomRight"
                        overlayStyle={{ marginTop: '-6px' }}
                        menu={{
                          items: [
                            {
                              type: 'group',
                              label: 'Options',
                              children: [
                                {
                                  key: 'edit',
                                  label: 'Edit',
                                  icon: <EditOutlined />,
                                  disabled: note?.rn_upload_type !== 'notes',
                                },
                                {
                                  key: 'delete',
                                  label: 'Delete',
                                  icon: <DeleteOutlined />,
                                },
                              ],
                            },
                          ],
                          onClick: ({ key, domEvent }) => {
                            domEvent.preventDefault();
                            domEvent.stopPropagation();

                            if (key === 'edit' && onEditNote) {
                              onEditNote(note, viewNotesModal?.details);
                            }

                            if (key === 'delete' && onDeleteNote) {
                              setPendingDeleteNoteId(note?.rn_id);
                            }
                          },
                        }}
                        trigger={['click']}
                      >
                        <Popconfirm
                          title="Delete note?"
                          description="This action will delete the selected note."
                          okText="Yes"
                          cancelText="No"
                          okType="danger"
                          open={pendingDeleteNoteId === note?.rn_id}
                          onConfirm={event => {
                            event?.preventDefault();
                            event?.stopPropagation();
                            setPendingDeleteNoteId(null);
                            onDeleteNote(note, viewNotesModal?.details);
                          }}
                          onCancel={event => {
                            event?.preventDefault();
                            event?.stopPropagation();
                            setPendingDeleteNoteId(null);
                          }}
                          onOpenChange={open => {
                            if (!open && pendingDeleteNoteId === note?.rn_id) {
                              setPendingDeleteNoteId(null);
                            }
                          }}
                          overlayStyle={{ width: 300 }}
                          placement="topRight"
                        >
                          <span
                            className="action-icon-button"
                            onClick={event => {
                              event.preventDefault();
                              event.stopPropagation();
                              if (!areSameNote(note.rn_id, selectedNote?.rn_id)) {
                                handleNoteSelection(note, viewNotesModal?.details);
                              }
                            }}
                          >
                            <MoreOutlined style={iconStyle} />
                          </span>
                        </Popconfirm>
                      </Dropdown>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </Col>
        <Col span={16}>
          <div
            className="notes-detail"
            style={{ height: '425px', overflowY: 'auto' }}
          >
            {selectedNote?.rn_upload_type === 'notes' ? (
              areSameNote(editingNoteId, selectedNote?.rn_id) ? (
                <div style={{ padding: '5px' }}>
                  <TextArea
                    value={editedNoteText}
                    rows={12}
                    onChange={event => {
                      setEditedNoteText(event.target.value);
                    }}
                    disabled={savingNote}
                  />
                </div>
              ) : (
                <div style={{ padding: '5px' }}>{selectedNote?.rn_notes}</div>
              )
            ) : (
              selectedNote?.rn_upload_path && (
                <PdfViewer
                  pdfArrayBuffer={pdfData}
                  refreshing={loadingPdf}
                  patient_id={patient?.pat_id}
                  order_id={pacs_order?.pacs_ord_id}
                  patientData={{
                    patient: patient,
                    ps_study_dt_tm: selectedNote?.createdAt,
                  }}
                  onReportUpdated
                  hideCopyButton={true}
                  hideWhatsappButton={true}
                  mimeType={mimeType}
                />
              )
            )}
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default ViewNotes;
